/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import type React from 'react';

// Returns true when a keyboard event was fired as part of an active IME
// composition session (e.g. selecting a candidate while typing Japanese,
// Chinese, or Korean). Enter/Tab/Arrow keys that finalize a candidate must
// not be treated as submit/navigation commands.
//
// Why both checks:
//   - `nativeEvent.isComposing` is the W3C signal and works in Chromium and
//     WebKit.
//   - `keyCode === 229` is the legacy IME sentinel. Firefox finalizes the
//     composition ~10–20ms before the Enter keydown fires, so `isComposing`
//     is already false by the time we see the event, but `keyCode` is still
//     229. Keeping this fallback preserves correctness on Firefox/Gecko.
export function isIMEComposing(
	event: React.KeyboardEvent | KeyboardEvent | {nativeEvent?: KeyboardEvent; keyCode?: number; isComposing?: boolean},
): boolean {
	const native = 'nativeEvent' in event ? event.nativeEvent : (event as KeyboardEvent);
	if (native?.isComposing) return true;
	const keyCode = 'keyCode' in event && typeof event.keyCode === 'number' ? event.keyCode : native?.keyCode;
	return keyCode === 229;
}
